import React, { useEffect, useState } from 'react';
import { Layout, Button, Drawer, Flex, Grid, Typography, Badge, Modal } from 'antd';
import { UserOutlined, MenuOutlined, ShopOutlined, CarOutlined, ShoppingCartOutlined, LogoutOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { OrderService } from '../../services/OrderService';
import { OrderItem } from '../../models/OrderItem';
import logo from '../../assets/logo-logo.png';

const { Header } = Layout;
const { useBreakpoint } = Grid;
const { Text } = Typography;

// Kept in one place so every page relying on the nav's fixed height
// (LandingPage's hero offset, LogInPage's calc(100dvh - 64px)) stays in sync.
export const HEADER_HEIGHT = 64;

const navLinks = [
    { key: 'vacancies', label: 'Vacancies',        icon: <UserOutlined />, path: '/VacanciesPage' },
    { key: 'courier',   label: 'Become a courier',  icon: <CarOutlined />,  path: '/becomeADriverPage' },
    { key: 'seller',    label: 'Become a Seller',   icon: <ShopOutlined />, path: '/sellerRegisterPage' },
];

interface TopNavigationProps {
    isLoggedIn?: boolean;
    cartItemCount?: number;
    onCartClick?: () => void;
}

const TopNavigation: React.FC<TopNavigationProps> = ({ isLoggedIn, cartItemCount, onCartClick }) => {
    const navigate = useNavigate();
    const screens = useBreakpoint();
    const [drawerOpen, setDrawerOpen] = useState(false);

    const [loggedInFromStorage] = useState(() => !!localStorage.getItem('access'));
    const loggedIn = isLoggedIn ?? loggedInFromStorage;

    const [fetchedCartCount, setFetchedCartCount] = useState(0);

    useEffect(() => {
        if (cartItemCount !== undefined) return;
        if (!loggedIn) return;

        OrderService.readCartByUser()
            .then((items: OrderItem[]) => {
                const count = items.reduce((sum, oi) => sum + oi.quantity, 0);
                setFetchedCartCount(count);
            })
            .catch(() => {});
    }, [cartItemCount, loggedIn]);

    const displayedCartCount = cartItemCount ?? fetchedCartCount;

    const isDesktop = screens.md;

    const goTo = (path: string) => {
        setDrawerOpen(false);
        navigate(path);
    };

    const handleLogout = () => {
        localStorage.removeItem('access');
        localStorage.removeItem('refresh');
        localStorage.removeItem('role');
        setDrawerOpen(false);
        navigate('/logIn');
    };

    const handleCartClick = () => {
        if (onCartClick) {
            onCartClick();
        } else {
            navigate('/cart');
        }
    };

    // Same pattern as LogInPage's goToRestaurantsWithLocation — kept here so the
    // logo click can go straight to nearby restaurants for a logged-in user.
    const goToRestaurantsWithLocation = () => {
        if (!navigator.geolocation) {
            Modal.warning({
                title: "Location unavailable",
                content: "Your browser doesn't support location services. You can still browse, but nearby results may not be accurate.",
                onOk: () => navigate("/restaurantsPage"),
            });
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const lat = position.coords.latitude;
                const lng = position.coords.longitude;
                navigate(`/restaurantsPage/${lat}/${lng}`);
            },
            (error) => {
                if (error.code === error.PERMISSION_DENIED) {
                    Modal.warning({
                        title: "Location access needed",
                        content: "Please enable location permissions so we can show restaurants near you.",
                        onOk: () => navigate("/restaurantsPage"),
                    });
                } else {
                    Modal.warning({
                        title: "Location unavailable",
                        content: "We couldn't determine your location right now. You can still browse and set your address manually.",
                        onOk: () => navigate("/restaurantsPage"),
                    });
                }
            }
        );
    };

    const handleLogoClick = () => {
        if (loggedIn) {
            goToRestaurantsWithLocation();
        } else {
            navigate('/');
        }
    };

    const CartBadge = (
        <Badge
            count={displayedCartCount}
            offset={[-4, 4]}
            style={{
                backgroundColor: '#ff4d4f',
                minWidth: 22,
                height: 22,
                borderRadius: 11,
                fontSize: 13,
                fontWeight: 700,
                lineHeight: '22px',
                padding: '0 6px',
            }}
        >
            <Button
                type="text"
                icon={<ShoppingCartOutlined style={{ fontSize: 22 }} />}
                onClick={handleCartClick}
                aria-label="Cart"
            />
        </Badge>
    );

    return (
        <Header
            style={{
                position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
                height: HEADER_HEIGHT, lineHeight: 'normal',
                background: '#fff',
                boxShadow: '0 2px 12px rgba(0,175,102,0.12)',
                borderBottom: '2px solid #E6F7EF',
                padding: `0 ${screens.xs ? 16 : 24}px`,
            }}
        >
            <Flex align="center" justify="space-between" style={{ height: '100%' }}>

                <img
                    src={logo} alt="logo"
                    style={{ height: 40, cursor: 'pointer' }}
                    onClick={handleLogoClick}
                />

                {isDesktop ? (
                    <Flex align="center" gap={28}>
                        {navLinks.map((link) => (
                            <Flex
                                key={link.key}
                                align="center"
                                gap={6}
                                style={{ cursor: 'pointer', fontWeight: 600, fontSize: 15 }}
                                onClick={() => goTo(link.path)}
                            >
                                {link.icon}
                                <Text style={{ fontWeight: 600 }}>{link.label}</Text>
                            </Flex>
                        ))}

                        {loggedIn && CartBadge}

                        {loggedIn ? (
                            <Button
                                icon={<LogoutOutlined />}
                                size="large"
                                onClick={handleLogout}
                                style={{ fontWeight: 700 }}
                            >
                                Logout
                            </Button>
                        ) : (
                            <Button type="primary" size="large" onClick={() => navigate('/logIn')} style={{ fontWeight: 700 }}>
                                Log In
                            </Button>
                        )}
                    </Flex>
                ) : (
                    <Flex align="center" gap={8}>
                        {loggedIn && CartBadge}
                        <Button
                            type="text"
                            icon={<MenuOutlined style={{ fontSize: 20 }} />}
                            onClick={() => setDrawerOpen(true)}
                            aria-label="Open menu"
                        />
                    </Flex>
                )}

            </Flex>

            <Drawer
                title={<img src={logo} alt="logo" style={{ height: 32 }} />}
                placement="right"
                onClose={() => setDrawerOpen(false)}
                open={drawerOpen}
                width={280}
            >
                <Flex vertical gap={4}>
                    {navLinks.map((link) => (
                        <Button
                            key={link.key}
                            type="text"
                            icon={link.icon}
                            onClick={() => goTo(link.path)}
                            style={{ justifyContent: 'flex-start', height: 44, fontWeight: 600 }}
                            block
                        >
                            {link.label}
                        </Button>
                    ))}

                    {loggedIn ? (
                        <Button
                            danger
                            type="primary"
                            icon={<LogoutOutlined />}
                            size="large"
                            onClick={handleLogout}
                            style={{ fontWeight: 700, marginTop: 12 }}
                            block
                        >
                            Logout
                        </Button>
                    ) : (
                        <Button
                            type="primary"
                            size="large"
                            onClick={() => goTo('/logIn')}
                            style={{ fontWeight: 700, marginTop: 12 }}
                            block
                        >
                            Log In
                        </Button>
                    )}

                </Flex>
            </Drawer>
        </Header>
    );
};

export default TopNavigation;